"use client";

import React, { useEffect, useRef, useState } from "react";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { useRevealOnScroll } from "@/hooks/useRevealOnScroll";
const steps = [{
  id: 1,
  number: "01",
  title: "Strategie & Analyse"
}, {
  id: 2,
  number: "02",
  title: "Design & UX Optimalisatie"
}, {
  id: 3,
  number: "03",
  title: "Full-Stack Development"
}, {
  id: 4,
  number: "04",
  title: "Lancering & Groei"
}];
const stepPoints = [{
  stepId: 1,
  text: "Diepgaande sessie om uw bedrijfsdoelen en doelgroep scherp te stellen."
}, {
  stepId: 1,
  text: "Analyse van de markt en concurrentie voor een unieke online positionering."
}, {
  stepId: 1,
  text: "Vaststellen van de sitemap en technische vereisten voor een solide basis."
}, {
  stepId: 2,
  text: "Vertaling van de strategie naar een high-end, visueel interface ontwerp."
}, {
  stepId: 2,
  text: "Focus op intuïtieve navigatie en maximale conversie-ratio's in elk detail."
}, {
  stepId: 2,
  text: "Creatie van interactieve prototypes om de gebruikerservaring te perfectioneren."
}, {
  stepId: 3,
  text: "Ontwikkeling van een razendsnelle, responsieve en veilige web-architectuur."
}, {
  stepId: 3,
  text: "Schone code-implementatie volledig geoptimaliseerd voor zoekmachines (SEO)."
}, {
  stepId: 3,
  text: "Naadloze integratie van beheersystemen voor volledige controle over uw content."
}, {
  stepId: 4,
  text: "Uitgebreide kwaliteitscontrole op performance, veiligheid en browser-compatibiliteit."
}, {
  stepId: 4,
  text: "Begeleide livegang en een zorgvuldige overdracht van het volledige platform."
}, {
  stepId: 4,
  text: "Continue monitoring en ondersteuning voor blijvende digitale groei en succes."
}];
function TimelineItem({
  step,
  index,
  points
}: {
  step: typeof steps[0];
  index: number;
  points: typeof stepPoints;
}) {
  const {
    ref,
    isVisible
  } = useRevealOnScroll({
    threshold: 0.3
  });
  const isEven = index % 2 === 0;
  return <div data-section-id="113017" ref={ref} data-index={index} className={`relative flex flex-col md:flex-row items-center justify-between w-full mb-24 last:mb-0 ${isEven ? "md:flex-row-reverse" : ""}`}>
      {}
      <div className="absolute left-4 md:left-1/2 w-10 h-10 rounded-full -translate-x-1/2 flex items-center justify-center bg-background border-4 border-border z-20 transition-all duration-700 ease-out shadow-lg" style={{
      borderColor: isVisible ? "var(--color-primary)" : "",
      boxShadow: isVisible ? "0 0 20px var(--color-primary)" : "none"
    }}>
        <div className={`w-4 h-4 rounded-full transition-all duration-700 delay-200 ${isVisible ? "bg-primary scale-100" : "bg-transparent scale-0"}`} />
      </div>

      {}
      <div className={`w-full md:w-5/12 pl-16 md:pl-0 transition-all duration-700 transform ${isVisible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-12"} ${isEven ? "md:pr-16 md:text-right" : "md:pl-16 text-left"}`}>
        <Card className="relative overflow-hidden bg-card/40 backdrop-blur-xl border-white/10 hover:border-primary/40 transition-colors duration-500 shadow-2xl">
          {}
          <span className={`absolute -top-6 ${isEven ? "md:-left-4 right-4 md:right-auto" : "-right-4"} text-[120px] leading-none font-bold text-foreground/5 select-none pointer-events-none font-sans z-0`}>
            {step.number}
          </span>

          <CardHeader className="relative z-10 pb-2">
            <CardTitle className="text-2xl md:text-3xl font-sans font-bold tracking-tight text-primary-foreground">
              {step.title}
            </CardTitle>
          </CardHeader>
          <CardContent className="relative z-10">
            <ul className="space-y-4 mt-4">
              {points.map((p, i) => <li key={i} data-index={i} className={`flex items-start gap-4 ${isEven ? "md:flex-row-reverse" : ""}`}>
                  <span className="mt-2 w-2 h-2 rounded-full bg-primary shrink-0 shadow-[0_0_10px_var(--color-primary)]" />
                  <span className="text-muted-foreground leading-relaxed text-base font-sans">
                    {p.text}
                  </span>
                </li>)}
            </ul>
          </CardContent>
        </Card>
      </div>

      {}
      <div className="hidden md:block md:w-5/12" />
    </div>;
}
export default function DeJijop1Methode() {
  const [scrollProgress, setScrollProgress] = useState(0);
  const containerRef = useRef<HTMLDivElement>(null);
  useEffect(() => {
    const handleScroll = () => {
      if (!containerRef.current) return;
      const rect = containerRef.current.getBoundingClientRect();
      const windowHeight = window.innerHeight;
      const totalHeight = rect.height;
      const scrolled = windowHeight / 2 - rect.top;
      let progress = scrolled / totalHeight * 100;
      progress = Math.max(0, Math.min(100, progress));
      setScrollProgress(progress);
    };
    window.addEventListener("scroll", handleScroll);
    handleScroll();
    return () => window.removeEventListener("scroll", handleScroll);
  }, []);
  return <section id="van-concept-tot-lancering" className="dark bg-background text-foreground py-24 md:py-32 px-4 sm:px-6 lg:px-8 relative overflow-hidden">
      {}
      <div className="absolute top-1/4 left-1/2 -translate-x-1/2 w-[800px] h-[800px] bg-primary/10 rounded-full blur-[120px] pointer-events-none" />
      <div className="absolute bottom-1/4 right-0 w-[400px] h-[400px] bg-secondary/5 rounded-full blur-[100px] pointer-events-none" />

      <div className="max-w-7xl mx-auto relative z-10">
        <div className="text-center mb-20 md:mb-32">
          <h2 className="text-4xl md:text-5xl font-sans font-bold tracking-tighter text-primary-foreground mb-6">
            Van Concept tot Lancering
          </h2>
          <p className="text-lg md:text-xl text-muted-foreground max-w-3xl mx-auto font-sans leading-relaxed">
            Een gestroomlijnd proces waarbij we creativiteit en techniek combineren om een digitale ervaring te bouwen die resultaat levert voor uw onderneming.
          </p>
        </div>

        <div className="relative" ref={containerRef}>
          {}
          <div className="absolute left-4 md:left-1/2 top-0 bottom-0 w-[2px] bg-border/20 -translate-x-1/2 rounded-full overflow-hidden z-0">
            {}
            <div className="absolute top-0 left-0 w-full bg-gradient-to-b from-primary to-secondary transition-all duration-100 ease-linear shadow-[0_0_15px_var(--color-primary)]" style={{
            height: `${scrollProgress}%`
          }} />
          </div>

          {}
          <div className="relative z-10 py-10">
            {steps.map((step, index) => {
            const points = stepPoints.filter(p => p.stepId === step.id);
            return <TimelineItem key={step.id} step={step} index={index} points={points} />;
          })}
          </div>
        </div>
      </div>
    </section>;
}